/**
 * Access Management — owner-only page.
 *
 * 4 tabs:
 *   1. Tab Access — per-user tab visibility (business/projects)
 *   2. Business Access — each business, who has access + role, grant/revoke
 *   3. Project Access — same for projects (isProject=true)
 *   4. Roles — CRUD roles + permission checkboxes
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Users, Shield, LayoutGrid, FolderOpen, UserPlus, Mail, Clock, Globe } from 'lucide-react';
import {
  collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { PERMISSIONS, SYSTEM_ROLE_IDS, OWNER_EMAIL, PORTALS } from '../lib/permissions';
import useRoles from '../hooks/useRoles';

const TABS = [
  { key: 'portal-access', label: 'Portal Access',    icon: Globe },
  { key: 'tab-access',    label: 'Tab Access',       icon: LayoutGrid },
  { key: 'business',      label: 'Business Access',  icon: Users },
  { key: 'project',       label: 'Project Access',   icon: FolderOpen },
  { key: 'invite',        label: 'Invite Users',     icon: UserPlus },
  { key: 'roles',         label: 'Roles',            icon: Shield },
];

export default function Access({ user, businesses, inviteToBusiness, getPendingInvites, revokeInvite, revokeAccess }) {
  const [activeTab, setActiveTab] = useState('portal-access');
  const { rolesById, roles } = useRoles(user);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <Link to="/" className="btn"><ArrowLeft size={16} /> Back</Link>
        <h1 style={{ margin: 0, fontSize: 22 }}>Access Management</h1>
        <span style={{ fontSize: 11, padding: '3px 10px', background: '#fee2a3', color: '#7a5c00', borderRadius: 10, textTransform: 'uppercase', fontWeight: 600 }}>Owner Only</span>
      </div>
      <p style={{ color: '#6b6b6b', marginTop: 4, marginBottom: 20, fontSize: 14 }}>
        Control who can see what, and what they can do. Changes apply immediately.
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e4e4e2', marginBottom: 20 }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className="btn"
              style={{
                borderRadius: '6px 6px 0 0',
                borderBottom: activeTab === t.key ? '2px solid #3a3a3a' : '2px solid transparent',
                background: activeTab === t.key ? '#fff' : 'transparent',
                fontWeight: activeTab === t.key ? 600 : 500,
                padding: '10px 16px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'portal-access' && <PortalAccessPanel user={user} />}
      {activeTab === 'tab-access' && <TabAccessPanel user={user} />}
      {activeTab === 'business' && <ItemAccessPanel user={user} businesses={businesses} isProject={false} rolesById={rolesById} revokeAccess={revokeAccess} />}
      {activeTab === 'project' && <ItemAccessPanel user={user} businesses={businesses} isProject={true} rolesById={rolesById} revokeAccess={revokeAccess} />}
      {activeTab === 'invite' && <InvitePanel user={user} businesses={businesses} rolesById={rolesById} inviteToBusiness={inviteToBusiness} getPendingInvites={getPendingInvites} revokeInvite={revokeInvite} />}
      {activeTab === 'roles' && <RolesPanel user={user} roles={roles} rolesById={rolesById} />}
    </div>
  );
}

// ---------------------------------------------------------------
// PORTAL ACCESS — central control of which external portals each
// user can reach (os.nexkoins, KNG OS, Ops OS, Ledger, UniHub).
// Writes users/{uid}.portalAccess[key] = bool. Each portal reads it
// on login (additive with its own legacy allowlist). See permissions.PORTALS.
// ---------------------------------------------------------------
function PortalAccessPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  // Owner-only account provisioning (Admin SDK behind the scenes — NOT self-serve).
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null); // { email, setPasswordLink, alreadyExisted }
  const [createErr, setCreateErr] = useState('');
  const [copied, setCopied] = useState(false);

  const createLogin = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setCreating(true);
    setCreateErr('');
    setCreated(null);
    try {
      const token = await user.getIdToken();
      const resp = await fetch('/.netlify/functions/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: newEmail.trim(), name: newName.trim(), password: newPassword }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed');
      setCreated({ ...data, usedPassword: newPassword }); // usedPassword kept client-side for the share message
      setNewEmail('');
      setNewName('');
      setNewPassword('');
      setCopied(false);
    } catch (err) {
      setCreateErr(err.message);
    }
    setCreating(false);
  };

  const shareMsg = created
    ? (created.passwordSet
        ? `Hi! Your NexKoins login is ready.\n\nSign in at https://os.nexkoins.com\nEmail: ${created.email}\nPassword: ${created.usedPassword}\n\nYou can change it later from your account.`
        : `Hi! Your NexKoins login is ready.\n\nSet your password here (one-time link): ${created.setPasswordLink}\n\nThen sign in at https://os.nexkoins.com with ${created.email} and your new password.`)
    : '';

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareMsg); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { alert('Copy failed'); }
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.email || '').localeCompare(b.email || '')));
      setLoading(false);
    });
    return unsub;
  }, []);

  const toggle = async (uid, key, value) => {
    setSaving(uid + key);
    try {
      const ref = doc(db, 'users', uid);
      const snap = await getDoc(ref);
      const current = snap.data()?.portalAccess || {};
      await updateDoc(ref, { portalAccess: { ...current, [key]: value } });
    } catch (e) {
      alert('Failed to save: ' + e.message);
    }
    setSaving('');
  };

  const createCard = (
    <div className="card" style={{ padding: 16, border: '1px solid #e4e4e2', borderRadius: 8, marginBottom: 16 }}>
      <strong>Create a login</strong>
      <div style={{ fontSize: 12, color: '#6b6b6b', margin: '4px 0 10px' }}>
        Owner-only (self-signup is disabled). Set a password to hand them directly, or leave it blank
        to generate a one-time set-password link instead. Works for a new person or to reset an existing one.
      </div>
      <form onSubmit={createLogin} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e4e4e2', borderRadius: 6, padding: '6px 10px' }}>
          <Mail size={14} color="#6b6b6b" />
          <input type="email" required placeholder="person@nexkoins.com" value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14 }} />
        </div>
        <input type="text" placeholder="Name (optional)" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ flex: '0 1 150px', padding: 8, border: '1px solid #e4e4e2', borderRadius: 6, fontSize: 14 }} />
        <input type="text" placeholder="Password (optional)" value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)} minLength={6} autoComplete="new-password"
          style={{ flex: '0 1 170px', padding: 8, border: '1px solid #e4e4e2', borderRadius: 6, fontSize: 14 }} />
        <button type="submit" className="btn btn-primary" disabled={creating}>
          <UserPlus size={14} /> {creating ? 'Saving…' : 'Create login'}
        </button>
      </form>
      {createErr && <div style={{ marginTop: 10, padding: 10, background: '#fdecea', color: '#b3261e', borderRadius: 6, fontSize: 13 }}>{createErr}</div>}
      {created && (
        <div style={{ marginTop: 12, padding: 12, background: '#f5faf6', border: '1px solid #d4e2d8', borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>
              {created.alreadyExisted ? `${created.email} already had an account` : `Created ${created.email}`} — send them this
            </strong>
            <button className="btn btn-sm btn-primary" onClick={copyShare}>{copied ? '✓ Copied' : 'Copy message'}</button>
          </div>
          <textarea readOnly value={shareMsg} rows={5}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 10, border: '1px solid #d4e2d8', borderRadius: 6, background: '#fff', color: '#3a3a3a', resize: 'vertical' }} />
          <div style={{ marginTop: 8, fontSize: 11, color: '#6b6b6b' }}>
            They now appear in the grid below — tick the portals they should reach.
            {created.passwordSet ? ' Password is set — they can log in right away.' : ' The set-password link is one-time and expires.'}
          </div>
        </div>
      )}
    </div>
  );

  if (loading) return <p style={{ color: '#6b6b6b' }}>Loading…</p>;

  if (!users.length) {
    return (
      <div>
        {createCard}
        <p style={{ color: '#6b6b6b' }}>
          No users yet. Use <strong>Create a login</strong> above to provision someone, then grant them portals here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {createCard}
      <div className="card" style={{ padding: 0, overflow: 'auto', border: '1px solid #e4e4e2', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafaf8' }}>
              <th style={{ padding: 12, textAlign: 'left', position: 'sticky', left: 0, background: '#fafaf8' }}>User</th>
              {PORTALS.map((p) => (
                <th key={p.key} style={{ padding: 12, textAlign: 'center', minWidth: 110 }} title={`${p.url} — ${p.description}`}>
                  {p.label}
                  <div style={{ fontSize: 10, color: '#9a9a9a', fontWeight: 400 }}>{p.url}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isOwner = u.email === OWNER_EMAIL;
              return (
                <tr key={u.id} style={{ borderTop: '1px solid #e4e4e2' }}>
                  <td style={{ padding: 12, position: 'sticky', left: 0, background: '#fff' }}>
                    {u.email || <span style={{ color: '#9a9a9a' }}>{u.id}</span>}
                    {isOwner && <span style={{ fontSize: 10, padding: '2px 6px', background: '#fee2a3', color: '#7a5c00', borderRadius: 10, marginLeft: 6 }}>OWNER</span>}
                  </td>
                  {PORTALS.map((p) => (
                    <td key={p.key} style={{ padding: 12, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        disabled={isOwner || saving === u.id + p.key}
                        checked={isOwner || u.portalAccess?.[p.key] === true}
                        onChange={(e) => toggle(u.id, p.key, e.target.checked)}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p style={{ padding: '12px 2px', fontSize: 12, color: '#6b6b6b', margin: 0 }}>
        Owner ({OWNER_EMAIL}) always has every portal. Tick a box to grant that person that portal; untick to revoke.
        Changes apply on their next login. During migration each portal also honors its old owner list, so nobody is locked out mid-rollout.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------
// TAB ACCESS
// ---------------------------------------------------------------
function TabAccessPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.email || '').localeCompare(b.email || '')));
      setLoading(false);
    });
    return unsub;
  }, []);

  const toggle = async (uid, key, value) => {
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    const current = snap.data()?.tabAccess || {};
    await updateDoc(ref, { tabAccess: { ...current, [key]: value } });
  };

  if (loading) return <p style={{ color: '#6b6b6b' }}>Loading…</p>;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid #e4e4e2', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#fafaf8' }}>
            <th style={{ padding: 12, textAlign: 'left' }}>User</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Business Tab</th>
            <th style={{ padding: 12, textAlign: 'center' }}>Projects Tab</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isOwner = u.email === OWNER_EMAIL;
            return (
              <tr key={u.id} style={{ borderTop: '1px solid #e4e4e2' }}>
                <td style={{ padding: 12 }}>
                  {u.email} {isOwner && <span style={{ fontSize: 10, padding: '2px 6px', background: '#fee2a3', color: '#7a5c00', borderRadius: 10, marginLeft: 6 }}>OWNER</span>}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <input type="checkbox" disabled={isOwner} checked={u.tabAccess?.business !== false}
                    onChange={(e) => toggle(u.id, 'business', e.target.checked)} />
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  <input type="checkbox" disabled={isOwner} checked={u.tabAccess?.projects !== false}
                    onChange={(e) => toggle(u.id, 'projects', e.target.checked)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={{ padding: 12, fontSize: 12, color: '#6b6b6b', margin: 0, borderTop: '1px solid #e4e4e2' }}>
        Owner always has all tabs. Uncheck to hide a tab from a user.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------
// BUSINESS / PROJECT ACCESS
// ---------------------------------------------------------------
function ItemAccessPanel({ user, businesses, isProject, rolesById }) {
  const items = useMemo(
    () => (businesses || []).filter((b) => !!b.isProject === isProject),
    [businesses, isProject]
  );

  const changeRole = async (bizId, uid, newRoleId) => {
    const ref = doc(db, 'businesses', bizId);
    const snap = await getDoc(ref);
    const roles = snap.data()?.roles || {};
    roles[uid] = newRoleId;
    await updateDoc(ref, { roles });
  };

  const removeUser = async (bizId, uid) => {
    const ref = doc(db, 'businesses', bizId);
    const snap = await getDoc(ref);
    const roles = { ...(snap.data()?.roles || {}) };
    delete roles[uid];
    await updateDoc(ref, { roles });
  };

  if (items.length === 0) {
    return <p style={{ color: '#6b6b6b' }}>No {isProject ? 'projects' : 'businesses'} yet.</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {items.map((b) => (
        <div key={b.id} className="card" style={{ padding: 0, border: '1px solid #e4e4e2', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: '#fafaf8', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{b.name} <span style={{ color: '#6b6b6b', fontWeight: 400, fontSize: 12 }}>({Object.keys(b.roles || {}).length} users)</span></span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fff' }}>
                <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e4e4e2' }}>User UID</th>
                <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e4e4e2' }}>Role</th>
                <th style={{ padding: 10, borderBottom: '1px solid #e4e4e2' }}></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(b.roles || {}).map(([uid, roleId]) => (
                <tr key={uid} style={{ borderTop: '1px solid #f0f0ed' }}>
                  <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 11 }}>{uid}</td>
                  <td style={{ padding: 10 }}>
                    <select value={roleId} onChange={(e) => changeRole(b.id, uid, e.target.value)} style={{ padding: 4 }}>
                      {Object.values(rolesById || {}).map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 10, textAlign: 'right' }}>
                    <button className="btn btn-sm danger" onClick={() => {
                      if (window.confirm(`Remove access for ${uid}?`)) removeUser(b.id, uid);
                    }}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <p style={{ fontSize: 12, color: '#6b6b6b' }}>
        To grant access to a new user, use the Invite flow from the business page. This panel manages existing users.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------
// ROLES CRUD
// ---------------------------------------------------------------
function RolesPanel({ user, roles, rolesById }) {
  const [editing, setEditing] = useState(null); // role being edited

  const openNew = () => {
    const id = `custom_${Date.now().toString(36)}`;
    setEditing({
      id, name: '', description: '', isSystem: false,
      permissions: Object.fromEntries(PERMISSIONS.map((p) => [p.key, false])),
      isNew: true,
    });
  };

  const save = async (role) => {
    if (!role.name.trim()) { alert('Name required'); return; }
    await setDoc(doc(db, 'roles', role.id), {
      name: role.name.trim(),
      description: role.description || '',
      isSystem: !!role.isSystem,
      permissions: role.permissions || {},
    }, { merge: !role.isNew });
    setEditing(null);
  };

  const remove = async (role) => {
    if (role.isSystem) { alert('System roles cannot be deleted'); return; }
    if (!window.confirm(`Delete role "${role.name}"? Users assigned this role will lose it and must be given a new role.`)) return;
    await deleteDoc(doc(db, 'roles', role.id));
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: '#6b6b6b' }}>{roles.length} roles · {roles.filter((r) => r.isSystem).length} system, {roles.filter((r) => !r.isSystem).length} custom</div>
        <button className="btn btn-primary" onClick={openNew}><Plus size={14} /> New role</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {roles.map((r) => (
          <div key={r.id} className="card" style={{ padding: 14, border: '1px solid #e4e4e2', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <strong>{r.name}</strong>
              {r.isSystem ? (
                <span style={{ fontSize: 10, padding: '2px 8px', background: '#e4e4e2', color: '#6b6b6b', borderRadius: 10, textTransform: 'uppercase' }}>System</span>
              ) : (
                <span style={{ fontSize: 10, padding: '2px 8px', background: '#f3efe7', color: '#7a5c00', borderRadius: 10, textTransform: 'uppercase' }}>Custom</span>
              )}
            </div>
            <div style={{ color: '#6b6b6b', fontSize: 12, marginBottom: 10 }}>{r.description || '—'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
              {PERMISSIONS.filter((p) => r.permissions?.[p.key]).map((p) => (
                <span key={p.key} style={{ fontSize: 10, padding: '2px 7px', background: '#eef4f0', color: '#1f7a4d', borderRadius: 10 }}>{p.label}</span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" onClick={() => setEditing({ ...r })}>Edit</button>
              {!r.isSystem && <button className="btn btn-sm danger" onClick={() => remove(r)}><Trash2 size={12} /></button>}
            </div>
          </div>
        ))}
      </div>

      {editing && <RoleEditorModal role={editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------
// INVITE PANEL — invite new users via email
// ---------------------------------------------------------------
function InvitePanel({ user, businesses, rolesById, inviteToBusiness, getPendingInvites, revokeInvite }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('viewer');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [pending, setPending] = useState([]);
  const [lastInvite, setLastInvite] = useState(null); // { email, roleName, names }
  const [copied, setCopied] = useState(false);

  const ownedBusinesses = useMemo(
    () => (businesses || []).filter((b) => b.roles?.[user.uid] === 'owner'),
    [businesses, user.uid]
  );

  useEffect(() => {
    if (!selectedIds.length || !getPendingInvites) { setPending([]); return; }
    Promise.all(selectedIds.map((id) => getPendingInvites(id))).then((res) => setPending(res.flat()));
  }, [selectedIds, getPendingInvites, msg]);

  const toggleBiz = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const selectAll = () =>
    setSelectedIds(selectedIds.length === ownedBusinesses.length ? [] : ownedBusinesses.map((b) => b.id));

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!selectedIds.length || !email.trim()) return;
    setSending(true);
    const names = [];
    try {
      for (const id of selectedIds) {
        await inviteToBusiness(id, email.trim(), roleId);
        const b = businesses.find((x) => x.id === id);
        if (b) names.push(b.name);
      }
      const roleName = rolesById?.[roleId]?.name || roleId;
      setMsg(`Invited ${email} as ${roleName} to ${names.join(', ')}`);
      setLastInvite({ email: email.trim(), roleName, names });
      setEmail('');
      setCopied(false);
    } catch (err) { alert('Failed: ' + err.message); }
    setSending(false);
  };

  const shareMessage = lastInvite
    ? `Hi! You've been invited to access ${lastInvite.names.join(', ')} on UniHub.\n\nSign up at https://hub.nexkoins.com/auth using ${lastInvite.email}. You'll automatically have access once you log in.\n\nIf you have any questions, let me know.`
    : '';

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { alert('Copy failed'); }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Cancel this pending invite?')) return;
    await revokeInvite(id);
    setMsg('refresh-' + Date.now());
  };

  if (!ownedBusinesses.length) {
    return <p style={{ color: '#6b6b6b' }}>You don't own any businesses to invite users to.</p>;
  }

  return (
    <div>
      <div className="card" style={{ padding: 16, border: '1px solid #e4e4e2', borderRadius: 8, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong>Select businesses / projects to invite to</strong>
          <button className="btn btn-sm" onClick={selectAll}>
            {selectedIds.length === ownedBusinesses.length ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {ownedBusinesses.map((b) => (
            <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, border: `1px solid ${selectedIds.includes(b.id) ? '#3a3a3a' : '#e4e4e2'}`, borderRadius: 6, cursor: 'pointer', background: selectedIds.includes(b.id) ? '#f3efe7' : '#fff' }}>
              <input type="checkbox" checked={selectedIds.includes(b.id)} onChange={() => toggleBiz(b.id)} />
              <span style={{ flex: 1, fontSize: 13 }}>{b.name}</span>
              {b.isProject && <span style={{ fontSize: 10, padding: '1px 6px', background: '#e4e4e2', borderRadius: 8, color: '#6b6b6b' }}>Project</span>}
            </label>
          ))}
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="card" style={{ padding: 16, border: '1px solid #e4e4e2', borderRadius: 8, marginBottom: 16 }}>
          <strong>Invite new user</strong>
          <form onSubmit={handleInvite} style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e4e4e2', borderRadius: 6, padding: '6px 10px' }}>
              <Mail size={14} color="#6b6b6b" />
              <input type="email" required placeholder="user@example.com" value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14 }} />
            </div>
            <select value={roleId} onChange={(e) => setRoleId(e.target.value)} style={{ padding: 8, border: '1px solid #e4e4e2', borderRadius: 6 }}>
              {Object.values(rolesById || {}).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <button type="submit" className="btn btn-primary" disabled={sending}>
              <UserPlus size={14} /> {sending ? 'Sending…' : 'Invite'}
            </button>
          </form>
          {msg && !msg.startsWith('refresh') && (
            <div style={{ marginTop: 10, padding: 10, background: '#e4f2e7', color: '#1f7a4d', borderRadius: 6, fontSize: 13 }}>{msg}</div>
          )}
        </div>
      )}

      {lastInvite && (
        <div className="card" style={{ padding: 16, border: '1px solid #d4e2d8', borderRadius: 8, marginBottom: 16, background: '#f5faf6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Send this to {lastInvite.email}</strong>
            <button className="btn btn-sm btn-primary" onClick={copyShare}>
              {copied ? '✓ Copied' : 'Copy message'}
            </button>
          </div>
          <textarea
            readOnly
            value={shareMessage}
            rows={5}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13, padding: 10, border: '1px solid #d4e2d8', borderRadius: 6, background: '#fff', color: '#3a3a3a', resize: 'vertical' }}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: '#6b6b6b' }}>
            Paste this in Telegram, WhatsApp, or email. They'll get access automatically when they sign up with this email.
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="card" style={{ padding: 16, border: '1px solid #e4e4e2', borderRadius: 8 }}>
          <strong>Pending invites ({pending.length})</strong>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
            {pending.map((inv) => {
              const biz = businesses.find((b) => b.id === inv.businessId);
              return (
                <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: '#fef8e7', borderRadius: 6, fontSize: 13 }}>
                  <Clock size={14} color="#b6934a" />
                  <span style={{ flex: 1 }}>{inv.email}</span>
                  <span style={{ color: '#6b6b6b', fontSize: 12 }}>{biz?.name || inv.businessId}</span>
                  <span style={{ fontSize: 11, padding: '2px 8px', background: '#fff', borderRadius: 10 }}>{inv.role}</span>
                  <button className="btn btn-sm danger" onClick={() => handleRevoke(inv.id)} title="Cancel invite"><Trash2 size={12} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RoleEditorModal({ role, onSave, onCancel }) {
  const [r, setR] = useState(role);
  const togglePerm = (k) => setR({ ...r, permissions: { ...r.permissions, [k]: !r.permissions?.[k] } });
  const isSys = r.isSystem;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 640, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', padding: 24, borderRadius: 10, background: '#fff' }}>
        <h3 style={{ margin: 0, marginBottom: 14 }}>{r.isNew ? 'New role' : `Edit: ${r.name}`}</h3>
        {isSys && <div style={{ padding: 8, background: '#fef2cc', color: '#7a5c00', fontSize: 12, borderRadius: 6, marginBottom: 12 }}>
          System role. Name/description editable, permissions locked.
        </div>}
        <label style={{ display: 'block', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#6b6b6b', marginBottom: 4 }}>Name</div>
          <input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #e4e4e2', borderRadius: 6 }} />
        </label>
        <label style={{ display: 'block', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#6b6b6b', marginBottom: 4 }}>Description</div>
          <input value={r.description} onChange={(e) => setR({ ...r, description: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #e4e4e2', borderRadius: 6 }} />
        </label>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: '#6b6b6b', margin: '14px 0 6px' }}>Permissions</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
          {PERMISSIONS.map((p) => (
            <label key={p.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: 8, border: '1px solid #e4e4e2', borderRadius: 6, background: r.permissions?.[p.key] ? '#eef4f0' : '#fff', cursor: isSys ? 'not-allowed' : 'pointer' }}>
              <input type="checkbox" checked={!!r.permissions?.[p.key]} disabled={isSys} onChange={() => togglePerm(p.key)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: '#6b6b6b' }}>{p.description}</div>
              </div>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(r)}>Save</button>
        </div>
      </div>
    </div>
  );
}
